import { X } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export type ViewableMediaType = 'image' | 'video';

export interface ViewableMedia {
  url: string;
  type: ViewableMediaType;
  title: string;
  // Whatever the card showed under the name - an upload time, usually.
  subtitle?: string;
}

interface MediaViewerModalProps {
  media: ViewableMedia | null;
  onClose: () => void;
}

/**
 * Full-size view of one report attachment.
 *
 * The cards in the media sections are thumbnails at 60px, which is enough to
 * tell photos apart and useless for reading a licence plate or a house number
 * off one. Opening the file in a new tab would work, but it leaves the report
 * behind mid-call; this keeps the operator on the page.
 */
const MediaViewerModal = ({ media, onClose }: MediaViewerModalProps) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (!media) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [media, onClose]);

  if (!media) return null;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-6">
      {/* The backdrop is a button rather than a div with onClick: it is a real
          control (click-outside closes), and only a button answers the
          keyboard as well as the mouse. */}
      <button
        type="button"
        aria-label={t('cmn>master>close')}
        onClick={onClose}
        className="absolute inset-0 bg-black/70 cursor-default"
      />

      <div className="relative flex max-h-full w-[70%] flex-col gap-3 overflow-hidden rounded-[12px] border bg-db-card bg-db-border p-5">
        <div className="flex w-full items-start justify-between gap-3">
          <div className="flex min-w-0 flex-col">
            <span className="title1bold truncate" title={media.title}>
              {media.title}
            </span>
            {media.subtitle && <span className="title2 text-text-grey">{media.subtitle}</span>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('cmn>master>close')}
            className="shrink-0 cursor-pointer"
          >
            <X />
          </button>
        </div>

        <div className="flex min-h-0 w-full items-center justify-center overflow-hidden rounded-[10px] bg-black">
          {media.type === 'video' ? (
            // biome-ignore lint/a11y/useMediaCaption: operator-uploaded footage, there is no caption track
            <video
              src={media.url}
              controls
              autoPlay
              className="max-h-[70vh] w-full object-contain"
            />
          ) : (
            <img
              src={media.url}
              alt={media.title}
              className="max-h-[70vh] w-full object-contain"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default MediaViewerModal;
